/**
 * @typedef {Object} ValueWithUnit
 * @property {number} value - The numeric value.
 * @property {string} unit - The unit of measurement.
 * @property {string} read - The human-readable representation.
 */

/**
 * @typedef {Object} ValueMinMax
 * @property {string} min - The minimum value representation.
 * @property {string} mean - The mean value representation.
 * @property {string} max - The maximum value representation.
 */

/**
 * @typedef {Object} ChartDataItem
 * @property {string} date - The date string.
 * @property {Object.<string, ValueWithUnit|ValueMinMax>} [otherProps] - Other dynamic keys representing either a ValueWithUnit or an object with min, mean, max ValueWithUnit.
 */

export default class ChartsDataDto {
  static fromApi(data, areaKeys) {
    // so areaKeys contains the keys for which we need to extract min, mean, max
    // for example, areaKeys might be ['temperature', 'humidity']
    // the data object is like:
    // {"2024-01-01": {"temperatureMin": {value:10, unit: "C", read: "10C"}, "temperatureMean": {value:15, unit: "C", read: "15C"}, "temperatureMax": {value:20, unit: "C", read: "20C"}, "humidityMin": {value:20, unit: "%", read: "20%"}, "humidityMean": {value:40, unit: "%", read: "40%"}, "humidityMax": {value:50, unit: "%", read: "50%"}, "wind_speed": {value:20, unit: "m/s", read: "20m/s"}}, ...
    // So what i need is to end up with an object like this
    // {date: "2024-01-01", temperature: {min: {value:10, unit: "C", read: "10C"}, mean: {value:15, unit: "C", read: "15C"}, max: {value:20, unit: "C", read: "20C"}}, humidity: {min: {value:20, unit: "%", read: "20%"}, mean: {value:40, unit: "%", read: "40%"}, max: {value:50, unit: "%", read: "50%"}}, wind_speed: {value:20, unit: "m/s", read: "20m/s"}}
    const result = [];
    for (const [date, values] of Object.entries(data)) {
      const entry = { date };
      for (const [key, val] of Object.entries(values)) {
        // Check if this key is part of an area series
        const areaKey = areaKeys.find(
          (areaKey) =>
            key === `${areaKey}Min` ||
            key === `${areaKey}Mean` ||
            key === `${areaKey}Max`
        );
        if (areaKey) {
          // It's part of an area series
          if (!entry[areaKey]) {
            entry[areaKey] = {};
          }
          if (key.endsWith("Min")) {
            entry[areaKey].min = val;
          } else if (key.endsWith("Mean")) {
            entry[areaKey].mean = val;
          } else if (key.endsWith("Max")) {
            entry[areaKey].max = val;
          }
        } else {
          // Regular key, just copy it
          entry[key] = val;
        }
      }
      result.push(entry);
    }
    return result;
  }
}
